import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import Swal from 'sweetalert2';
import { CurrencyPipe } from '@angular/common';
import { NgbOffcanvas, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { UserService } from 'src/app/services/user/user.service';
import * as alertifyjs from 'alertifyjs';

interface Payment {
  'Codigo de pago': string;
  'Monto a favor': number;
  'Fecha de realizacion del pago': string;
  'posible_distribucion'?: Array<{
    _id: string;
    monto_aplicable: number;
    completado: boolean;
    tipo: string;
    numero_credito?: number;
    numero_cuota?: number;
  }>;
}

interface Fee {
  _id: string;
  'Deuda Fee': number;
  'Credito': string;
  'Numero de Cuota': number;
  'Fecha de vencimiento Cuota': string;
  'Deuda de cuota': number;
  'Estado': string;
  'Nombre': string;
  'Status': string;
  // Estado visual derivado (igual que contact-financial-payments):
  // 'complete' | 'awaiting-close' | 'pending'. Distingue el fee pagado que
  // espera el cierre de la cuota del que realmente sigue pendiente.
  _visualStatus?: string;
}

interface PendingInstallment {
  _id: string;
  dueBalance: number;
  tipo: string;
  numero_credito: number;
  numero_cuota: number;
}

interface PaymentResponse {
  'Pagos con saldo a favor': Payment[];
  'Fees': Fee[];
  'Cuotas pendientes': PendingInstallment[];
  montoTotalAFavor: number;
  deudaFees: number;
  deudaCreditos: number;
  deudaTotal: number;
}

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.scss']
})
export class PaymentListComponent implements OnInit {
  payments: Payment[] = [];
  fees: Fee[] = [];
  pendingInstallments: PendingInstallment[] = [];
  params_payment: any;
  montoTotalAFavor: number = 0;
  queryParams: any;
  loading = false;
  person: any;
  deudaFees: number = 0;
  deudaCreditos: number = 0;
  deudaTotal: number = 0;
  isProcessingPayment = false;
  selectedFeeFilter: string = 'Todos';

  // Opciones del filtro
  filterOptions = [
    { value: 'Todos', label: 'Todos' },
    { value: 'Pago Completado', label: 'Pago Completado' },
    { value: 'Pago Pendiente', label: 'Pago Pendiente' }
  ];

  // Getter para filtrar las penalidades
  get filteredFees(): Fee[] {
    if (this.selectedFeeFilter === 'Todos') {
      return this.fees;
    }
    return this.fees.filter(fee => fee['Status'] === this.selectedFeeFilter);
  }

  // Método para manejar el cambio del filtro
  onFeeFilterChange(event: any) {
    this.selectedFeeFilter = event.target.value;
  }

  @ViewChild('distributionPreviewModal') distributionPreviewModal!: TemplateRef<any>;
  
  previewDistribution: any = {
    fees: [],
    installments: []
  };

  currentPayment: Payment | null = null;

  constructor(
    private currencyPipe: CurrencyPipe,
    private offcanvasService: NgbOffcanvas,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private _user: UserService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.queryParams = params;
      if (this.queryParams?.code) {
        this.getPerson();
      }
    });
  }

  getPerson = async () => {
    this.loading = true;
    try {
      const resp = await this._user.getPersonbyCode({ code: this.queryParams.code });
      this.person = resp.resp;
      const resp_invoiceCredits = await this.listPaymentsPendingAmount(resp.resp);
      
      // Transformar la respuesta al formato esperado
      if (resp_invoiceCredits) {
        this.transformResponse(resp_invoiceCredits);
      }
    } catch (error) {
      console.error(error);
      alertifyjs.error('Error al cargar los datos');
    }
    this.loading = false;
  }

  private transformResponse(data: any) {
    // Transformar los datos al formato esperado
    const response: PaymentResponse = {
      'Pagos con saldo a favor': data['Pagos con saldo a favor']? data['Pagos con saldo a favor']: [] ,
      'Fees': data['Fees']? data['Fees']: [] ,
      'Cuotas pendientes': data['Cuotas pendientes']? data['Cuotas pendientes']: [],
      montoTotalAFavor: data['montoTotalAFavor'] ? data['montoTotalAFavor']: 0,
      deudaFees: data['deudaFees']? data['deudaFees']: 0,
      deudaCreditos: data['deudaCreditos']? data['deudaCreditos']: 0,
      deudaTotal: data['deudaTotal']? data['deudaTotal']: 0,
    };

    // Procesar los datos según la estructura del servicio
    if (data) {
      this.payments = response['Pagos con saldo a favor'];
      // Estado visual del fee (misma lógica que contact-financial-payments):
      // un fee con Status "Pago Pendiente" pero sin deuda propia (Deuda Fee <= 0)
      // ya está pago y solo espera el cierre de la cuota → "Completo · Esperando cierre".
      // Los exonerados/inactivos/anulados ya vienen excluidos del backend.
      this.fees = (response['Fees'] || []).map((fee: Fee) => {
        const dueFee = Number(fee['Deuda Fee'] ?? 0);
        if (fee['Status'] === 'Pago Completado') {
          fee._visualStatus = 'complete';
        } else if (fee['Status'] === 'Pago Pendiente' && dueFee <= 0) {
          fee._visualStatus = 'awaiting-close';
        } else if (fee['Status'] === 'Pago Pendiente') {
          fee._visualStatus = 'pending';
        } else {
          // Cualquier otro estado inesperado: se respeta el texto original.
          fee._visualStatus = 'other';
        }
        return fee;
      });
      this.pendingInstallments = response['Cuotas pendientes'];
      this.montoTotalAFavor = response['montoTotalAFavor'];
      this.deudaFees = response['deudaFees'];
      this.deudaCreditos = response['deudaCreditos'];
      this.deudaTotal = response['deudaTotal'];
    }
  }



  listPaymentsPendingAmount = async (resp: any) => {
    try {
      const data = {
        identity: resp.identitys[0].identity
      };
      return this._user.listPaymentsPendingAmount(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }


  openBottom(content: TemplateRef<any>, params_payment: any) {
    try {
      this.params_payment = params_payment;
      this.offcanvasService.open(content, {
        position: 'bottom',
        keyboard: false,
      });
    } catch (error) {
      console.log(error);
    }
  }

  response(event: any) {
    // Handle response if needed
    if (event) {
      this.getPerson(); // Recargar datos si es necesario
    }
  }

  confirmExoneration(fee: Fee): void {
    // Deshabilitar el botón y mostrar estado de carga
    const button = document.querySelector(`button[data-fee-id="${fee._id}"]`) as HTMLButtonElement;
    if (button) {
      button.disabled = true;
      button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Procesando...';
    }

    const formattedAmount = this.currencyPipe.transform(fee['Deuda Fee'], 'USD', 'symbol', '1.2-2');
    
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas exonerar la penalidad del crédito ${fee['Credito']} por un monto de ${formattedAmount}? Esta acción no se puede deshacer y todos los pagos aplicados se desconciliarán y quedarán con monto por aplicar.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, exonerar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        try {
          this._user.exonerarFee({id: fee._id}).then(() => {
            // Mostrar mensaje de éxito
            Swal.fire({
              title: '¡Exonerado!',
              text: 'La penalidad ha sido exonerada exitosamente.',
              icon: 'success',
              confirmButtonColor: '#28a745'
            }).then(() => {
              this.getPerson(); // Recargar datos después de la exoneración
            });
          }).catch((error: any) => {
            // Manejar error específico del servicio
            let errorMessage = 'Ha ocurrido un error al exonerar la penalidad.';
            if (error?.error?.message) {
              errorMessage = error.error.message;
            } else if (error?.message) {
              errorMessage = error.message;
            }
            
            Swal.fire({
              title: 'Error',
              text: errorMessage,
              icon: 'error',
              confirmButtonColor: '#dc3545'
            });
          }).finally(() => {
            // Restaurar el botón a su estado original
            if (button) {
              button.disabled = false;
              button.innerHTML = '<i class="fas fa-ban me-1"></i> Exonerar';
            }
          });
        } catch (error: any) {
          // Manejar errores generales
          console.error('Error al exonerar la penalidad:', error);
          Swal.fire({
            title: 'Error',
            text: 'Ha ocurrido un error inesperado al procesar la exoneración. Por favor, intente nuevamente.',
            icon: 'error',
            confirmButtonColor: '#dc3545'
          }).finally(() => {
            // Restaurar el botón a su estado original
            if (button) {
              button.disabled = false;
              button.innerHTML = '<i class="fas fa-ban me-1"></i> Exonerar';
            }
          });
        }
      } else {
        // Si el usuario cancela, restaurar el botón
        if (button) {
          button.disabled = false;
          button.innerHTML = '<i class="fas fa-ban me-1"></i> Exonerar';
        }
      }
    });
  }

  applyPayment(payment: Payment): void {
    // Deshabilitar el botón y mostrar estado de carga
    const button = document.querySelector(`button[data-payment-id="${payment['Codigo de pago']}"]`) as HTMLButtonElement;
    if (button) {
      button.disabled = true;
      button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Procesando...';
    }

    try {
      this._user.redistribuirPayment({code_payment: payment['Codigo de pago']}).then((response) => {
        // Mostrar mensaje de éxito
        Swal.fire({
          title: 'Finalizado!',
          text: response.msg || 'Pago aplicado exitosamente',
          icon: 'success',
          confirmButtonColor: '#28a745'
        }).then(() => {
          this.getPerson(); // Recargar datos después de aplicar el pago
        });
      }).catch((error: any) => {
        // Manejar error específico del servicio
        let errorMessage = 'Ha ocurrido un error al aplicar el pago.';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#dc3545'
        });
      }).finally(() => {
        // Restaurar el botón a su estado original
        if (button) {
          button.disabled = false;
          button.innerHTML = '<i class="fas fa-check me-1"></i> Aplicar Pago';
        }
        this.isProcessingPayment = false;
      });
    } catch (error: any) {
      // Manejar errores generales
      console.error('Error al aplicar el pago:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error inesperado al procesar el pago. Por favor, intente nuevamente.',
        icon: 'error',
        confirmButtonColor: '#dc3545'
      }).finally(() => {
        // Restaurar el botón a su estado original
        if (button) {
          button.disabled = false;
          button.innerHTML = '<i class="fas fa-check me-1"></i> Aplicar Pago';
        }
        this.isProcessingPayment = false;
      });
    }
  }

  openDistributionPreview(possibleDistribution: any[] | undefined, payment: Payment) {
    // Solo mostramos los items que realmente reciben monto (monto_aplicable > 0).
    // Un fee/cuota con deuda 0 no recibe distribución; el backend lo marca como
    // "completado" (monto_aplicable >= dueBalance → 0 >= 0) y se pintaba en verde
    // "Completará" con 0.00 USD, dando a entender que el pago se aplica ahí cuando
    // en realidad va a la cuota. Filtrarlos elimina esa ambigüedad visual.
    const distributable = (possibleDistribution || []).filter(item => Number(item?.monto_aplicable) > 0);

    if (!distributable.length) {
      Swal.fire({
        title: 'Sin distribución posible',
        text: 'No hay cuotas o fees disponibles para aplicar este pago.',
        icon: 'info',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Guardar el pago actual
    this.currentPayment = payment;

    // Separar fees y cuotas
    this.previewDistribution = {
      fees: distributable.filter(item => item.tipo === 'fee'),
      installments: distributable.filter(item => item.tipo === 'installment')
    };

    // Abrir el modal
    this.modalService.open(this.distributionPreviewModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  getTotalDistribution(): number {
    const allItems = [...this.previewDistribution.fees, ...this.previewDistribution.installments];
    return allItems.reduce((total: number, item: any) => {
      return total + (item.monto_aplicable || 0);
    }, 0);
  }

  confirmDistribution() {
    if (!this.currentPayment) return;

    Swal.fire({
      title: '¿Aplicar pago?',
      text: `¿Deseas aplicar el pago ${this.currentPayment['Codigo de pago']} por un monto de ${this.currencyPipe.transform(this.currentPayment['Monto a favor'], 'USD', 'symbol', '1.2-2')}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, aplicar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && this.currentPayment) {
        this.isProcessingPayment = true;
        this.applyPayment(this.currentPayment);
        this.currentPayment = null;
        this.modalService.dismissAll();
      }
    });
  }
} 